// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import type { VideoUploadResponse } from '../../../types/proctoring';

interface QueuedChunk {
  chunk: Blob;
  sessionId: string;
  chunkNumber: number;
  durationMs: number;
  retryCount: number;
}

export class VideoUploadService {
  private mediaRecorder: MediaRecorder | null = null;
  private currentChunkNumber = 0;
  private readonly CHUNK_DURATION_MS = 15000;
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly MAX_QUEUED_CHUNKS = 10;
  private retryQueue: QueuedChunk[] = [];
  private isUploading = false;
  private retryQueuePromise: Promise<void> | null = null;
  private uploadPromises: Promise<void>[] = [];
  private lastChunkStartedAt = 0;

  /**
   * Start recording video stream with chunked upload
   */
  startRecording(stream: MediaStream, sessionId: string): void {
    if (!stream || !sessionId) {
      console.error('Invalid stream or sessionId');
      return;
    }

    // Determine best codec available
    const mimeType = this.getSupportedMimeType();
    if (!mimeType) {
      console.error('No supported video codec found');
      return;
    }

    try {
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 450000,
        audioBitsPerSecond: 64000
      });

      this.mediaRecorder.ondataavailable = async (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          const now = Date.now();
          const durationMs = this.lastChunkStartedAt
            ? Math.max(0, now - this.lastChunkStartedAt)
            : this.CHUNK_DURATION_MS;
          this.lastChunkStartedAt = now;
          const uploadPromise = this.uploadChunk(event.data, sessionId, this.currentChunkNumber++, durationMs);
          this.uploadPromises.push(uploadPromise);
        }
      };

      this.mediaRecorder.onerror = (event: Event) => {
        console.error('MediaRecorder error:', event);
      };

      this.mediaRecorder.onstart = () => {
        this.lastChunkStartedAt = Date.now();
        console.log('Video recording started');
      };

      this.mediaRecorder.onstop = () => {
        console.log('Video recording stopped');
      };

      // Start recording with time slices for chunking
      this.mediaRecorder.start(this.CHUNK_DURATION_MS);
    } catch (error) {
      console.error('Failed to start MediaRecorder:', error);
    }
  }

  /**
   * Stop recording and upload final chunk
   */
  async stopRecording(): Promise<void> {
    const recorder = this.mediaRecorder;
    if (recorder && recorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        const previousOnStop = recorder.onstop;
        recorder.onstop = (event: Event) => {
          previousOnStop?.call(recorder, event);
          resolve();
        };
        recorder.requestData();
        recorder.stop();
      });
    }

    // Wait for all pending uploads to complete (max 30 seconds)
    try {
      await Promise.race([
        Promise.all(this.uploadPromises),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Upload timeout')), 30000))
      ]);
      console.log('All video chunks uploaded successfully');
    } catch (error) {
      console.error('Some chunks failed to upload:', error);
    }

    // Process retry queue
    await this.processRetryQueue();

    this.mediaRecorder = null;
    this.currentChunkNumber = 0;
    this.lastChunkStartedAt = 0;
    this.uploadPromises = [];
  }

  /**
   * Upload individual chunk to backend
   */
  private async uploadChunk(chunk: Blob, sessionId: string, chunkNumber: number, durationMs: number): Promise<void> {
    const formData = new FormData();
    formData.append('video', chunk, `chunk_${String(chunkNumber).padStart(4, '0')}.webm`);
    formData.append('sessionId', sessionId);
    formData.append('chunkNumber', String(chunkNumber));
    formData.append('durationMs', String(Math.round(durationMs)));

    try {
      const response = await fetch('/api/interview/video-chunk', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorBody = await response.text().catch(() => '');
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`
        );
      }

      const result: VideoUploadResponse = await response.json();
      console.log(`Chunk ${chunkNumber} uploaded:`, result.blobUrl);
    } catch (error) {
      console.error(`Failed to upload chunk ${chunkNumber}:`, error);
      this.queueForRetry(chunk, sessionId, chunkNumber, durationMs);
    }
  }

  /**
   * Queue failed chunk for retry with exponential backoff
   */
  private queueForRetry(chunk: Blob, sessionId: string, chunkNumber: number, durationMs: number): void {
    if (
      this.retryQueue.some(
        (queuedChunk) => queuedChunk.sessionId === sessionId && queuedChunk.chunkNumber === chunkNumber
      )
    ) {
      return;
    }

    if (this.retryQueue.length >= this.MAX_QUEUED_CHUNKS) {
      console.warn('Retry queue full, dropping oldest chunk');
      this.retryQueue.shift();
    }

    this.retryQueue.push({
      chunk,
      sessionId,
      chunkNumber,
      durationMs,
      retryCount: 0
    });

    // Start processing queue if not already running
    if (!this.isUploading) {
      this.processRetryQueue();
    }
  }

  /**
   * Process retry queue with exponential backoff
   */
  private async processRetryQueue(): Promise<void> {
    if (this.isUploading) {
      return this.retryQueuePromise || Promise.resolve();
    }

    if (this.retryQueue.length === 0) {
      return;
    }

    this.isUploading = true;
    this.retryQueuePromise = this.drainRetryQueue();
    try {
      await this.retryQueuePromise;
    } finally {
      this.isUploading = false;
      this.retryQueuePromise = null;
    }
  }

  private async drainRetryQueue(): Promise<void> {
    while (this.retryQueue.length > 0) {
      const queuedChunk = this.retryQueue[0];

      if (queuedChunk.retryCount >= this.MAX_RETRY_ATTEMPTS) {
        console.error(`Chunk ${queuedChunk.chunkNumber} failed after ${this.MAX_RETRY_ATTEMPTS} attempts`);
        this.retryQueue.shift();
        continue;
      }

      // Exponential backoff: 2s, 4s, 8s
      const delay = Math.pow(2, queuedChunk.retryCount) * 1000;
      await new Promise((resolve) => setTimeout(resolve, delay));

      try {
        await this.uploadChunkOnce(
          queuedChunk.chunk,
          queuedChunk.sessionId,
          queuedChunk.chunkNumber,
          queuedChunk.durationMs
        );
        this.retryQueue.shift();
      } catch (error) {
        queuedChunk.retryCount++;
        console.error(`Retry ${queuedChunk.retryCount} failed for chunk ${queuedChunk.chunkNumber}:`, error);
      }
    }
  }

  private async uploadChunkOnce(
    chunk: Blob,
    sessionId: string,
    chunkNumber: number,
    durationMs: number
  ): Promise<void> {
    const formData = new FormData();
    formData.append('video', chunk, `chunk_${String(chunkNumber).padStart(4, '0')}.webm`);
    formData.append('sessionId', sessionId);
    formData.append('chunkNumber', String(chunkNumber));
    formData.append('durationMs', String(Math.round(durationMs)));

    const response = await fetch('/api/interview/video-chunk', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(`Upload failed: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`);
    }
  }

  /**
   * Get supported MIME type for MediaRecorder
   */
  private getSupportedMimeType(): string | null {
    const types = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=h264,opus',
      'video/webm'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return null;
  }

  /**
   * Get current recording state
   */
  isRecording(): boolean {
    return this.mediaRecorder !== null && this.mediaRecorder.state === 'recording';
  }

  /**
   * Get retry queue status
   */
  getQueueStatus(): { pending: number; maxRetries: number } {
    return {
      pending: this.retryQueue.length,
      maxRetries: this.MAX_RETRY_ATTEMPTS
    };
  }
}
